/**
 * SCHEMA ÜRETİCİ FORM TANIMLARI.
 *
 * technicalseo.com/tools/schema-markup-generator mantığı: tip seçilir, alanlar
 * elle doldurulur, JSON-LD yazdıkça CANLI oluşur. Sayfadan AI ile üretmekten
 * farklı bir iş — orada kaynak sayfanın içeriği, burada kullanıcının elindeki
 * bilgi. İkisi de gerekli: elinde ürün fiyatı/etkinlik tarihi olan kullanıcı
 * bunu formdan girer, sayfada yazmıyor olabilir.
 *
 * Alan türleri render tarafında karşılanır. `liste` tekrarlanabilir alt kayıt
 * (SSS soruları, HowTo adımları, breadcrumb basamakları), `nesne` tek gömülü
 * kayıt (adres, yazar).
 */

export type AlanTuru = "metin" | "uzunMetin" | "url" | "tarih" | "tarihSaat" | "sayi" | "secim" | "nesne" | "liste";

export interface Alan {
  ad: string;                 // JSON-LD anahtarı
  etiket: string;             // kullanıcıya görünen
  tur: AlanTuru;
  zorunlu?: boolean;          // Google zengin sonuç için zorunlu
  ipucu?: string;
  ornek?: string;
  secenekler?: string[];      // tur=secim
  alanlar?: Alan[];           // tur=nesne | liste
  altTip?: string;            // gömülü nesnenin @type'ı
  enAz?: number;              // tur=liste — başlangıçta kaç satır
}

export interface SchemaFormu {
  tip: string;                // schema.org @type
  ad: string;                 // menüde görünen
  aciklama: string;
  alanlar: Alan[];
}

// Sık kullanılan gömülü nesneler
const ADRES: Alan = {
  ad: "address", etiket: "Adres", tur: "nesne", altTip: "PostalAddress", zorunlu: true,
  ipucu: "Google düz metin adresi kabul etmez, parçalı olmalı.",
  alanlar: [
    { ad: "streetAddress", etiket: "Sokak / cadde", tur: "metin", ornek: "Bağdat Cd. No:12" },
    { ad: "addressLocality", etiket: "İlçe / şehir", tur: "metin", ornek: "Kadıköy" },
    { ad: "addressRegion", etiket: "İl", tur: "metin", ornek: "İstanbul" },
    { ad: "postalCode", etiket: "Posta kodu", tur: "metin", ornek: "34710" },
    { ad: "addressCountry", etiket: "Ülke kodu", tur: "metin", ornek: "TR" },
  ],
};
const YAZAR: Alan = {
  ad: "author", etiket: "Yazar", tur: "nesne", altTip: "Person",
  ipucu: "Düz metin değil nesne olmalı — Google düz metin yazarı yok sayar.",
  alanlar: [
    { ad: "name", etiket: "Ad soyad", tur: "metin", ornek: "Dr. Ayşe Yılmaz" },
    { ad: "url", etiket: "Profil bağlantısı", tur: "url" },
  ],
};
const YAYINCI: Alan = {
  ad: "publisher", etiket: "Yayıncı", tur: "nesne", altTip: "Organization",
  alanlar: [
    { ad: "name", etiket: "Kurum adı", tur: "metin" },
    { ad: "logo", etiket: "Logo URL", tur: "url" },
  ],
};

export const SCHEMA_FORMLARI: SchemaFormu[] = [
  {
    tip: "Article", ad: "Makale", aciklama: "Blog yazısı, haber veya rehber içerik.",
    alanlar: [
      { ad: "headline", etiket: "Başlık", tur: "metin", zorunlu: true, ipucu: "110 karakteri aşmamalı." },
      { ad: "description", etiket: "Açıklama", tur: "uzunMetin" },
      { ad: "image", etiket: "Görsel URL", tur: "url" },
      { ad: "datePublished", etiket: "Yayın tarihi", tur: "tarih" },
      { ad: "dateModified", etiket: "Güncelleme tarihi", tur: "tarih" },
      YAZAR, YAYINCI,
      { ad: "mainEntityOfPage", etiket: "Sayfa URL", tur: "url" },
    ],
  },
  {
    tip: "BreadcrumbList", ad: "İçerik Yolu (Breadcrumb)", aciklama: "Ana sayfa › kategori › sayfa basamakları.",
    alanlar: [{
      ad: "itemListElement", etiket: "Basamaklar", tur: "liste", altTip: "ListItem", zorunlu: true, enAz: 2,
      ipucu: "Sıra otomatik verilir. Son basamağın URL'i boş bırakılabilir (bulunduğunuz sayfa).",
      alanlar: [
        { ad: "name", etiket: "Ad", tur: "metin", zorunlu: true, ornek: "Saç Ekimi" },
        { ad: "item", etiket: "URL", tur: "url" },
      ],
    }],
  },
  {
    tip: "FAQPage", ad: "SSS", aciklama: "Sıkça sorulan sorular. Soru-cevap sayfada GÖRÜNÜR olmalı.",
    alanlar: [{
      ad: "mainEntity", etiket: "Sorular", tur: "liste", altTip: "Question", zorunlu: true, enAz: 2,
      alanlar: [
        { ad: "name", etiket: "Soru", tur: "metin", zorunlu: true },
        { ad: "acceptedAnswer", etiket: "Cevap", tur: "uzunMetin", zorunlu: true },
      ],
    }],
  },
  {
    tip: "HowTo", ad: "Nasıl Yapılır", aciklama: "Adım adım anlatım.",
    alanlar: [
      { ad: "name", etiket: "Başlık", tur: "metin", zorunlu: true },
      { ad: "description", etiket: "Açıklama", tur: "uzunMetin" },
      { ad: "image", etiket: "Görsel URL", tur: "url" },
      { ad: "totalTime", etiket: "Toplam süre", tur: "metin", ipucu: "ISO 8601 süre — 30 dakika için PT30M, 2 saat için PT2H.", ornek: "PT30M" },
      {
        ad: "step", etiket: "Adımlar", tur: "liste", altTip: "HowToStep", zorunlu: true, enAz: 2,
        alanlar: [
          { ad: "name", etiket: "Adım başlığı", tur: "metin", zorunlu: true },
          { ad: "text", etiket: "Adım açıklaması", tur: "uzunMetin", zorunlu: true },
          { ad: "url", etiket: "Adım bağlantısı", tur: "url" },
          { ad: "image", etiket: "Adım görseli", tur: "url" },
        ],
      },
    ],
  },
  {
    tip: "Product", ad: "Ürün", aciklama: "Fiyat, stok ve puan bilgisiyle ürün.",
    alanlar: [
      { ad: "name", etiket: "Ürün adı", tur: "metin", zorunlu: true },
      { ad: "description", etiket: "Açıklama", tur: "uzunMetin" },
      { ad: "image", etiket: "Görsel URL", tur: "url" },
      { ad: "sku", etiket: "Stok kodu (SKU)", tur: "metin" },
      { ad: "brand", etiket: "Marka", tur: "nesne", altTip: "Brand", alanlar: [{ ad: "name", etiket: "Marka adı", tur: "metin" }] },
      {
        ad: "offers", etiket: "Fiyat bilgisi", tur: "nesne", altTip: "Offer",
        ipucu: "Zengin sonuç için fiyat, puan veya yorumdan en az biri gerekir.",
        alanlar: [
          { ad: "price", etiket: "Fiyat", tur: "metin", ornek: "1499.00" },
          { ad: "priceCurrency", etiket: "Para birimi", tur: "metin", ornek: "TRY" },
          { ad: "availability", etiket: "Stok durumu", tur: "secim", secenekler: ["https://schema.org/InStock", "https://schema.org/OutOfStock", "https://schema.org/PreOrder"] },
          { ad: "url", etiket: "Satın alma URL", tur: "url" },
        ],
      },
      {
        ad: "aggregateRating", etiket: "Toplu puan", tur: "nesne", altTip: "AggregateRating",
        alanlar: [
          { ad: "ratingValue", etiket: "Puan", tur: "metin", ornek: "4.7" },
          { ad: "reviewCount", etiket: "Yorum sayısı", tur: "sayi", ornek: "128" },
        ],
      },
    ],
  },
  {
    tip: "LocalBusiness", ad: "Yerel İşletme", aciklama: "Fiziksel adresi olan işletme.",
    alanlar: [
      { ad: "name", etiket: "İşletme adı", tur: "metin", zorunlu: true },
      ADRES,
      { ad: "telephone", etiket: "Telefon", tur: "metin", ornek: "+90 212 000 00 00" },
      { ad: "url", etiket: "Web sitesi", tur: "url" },
      { ad: "image", etiket: "Görsel URL", tur: "url" },
      { ad: "priceRange", etiket: "Fiyat aralığı", tur: "metin", ornek: "₺₺" },
      {
        ad: "geo", etiket: "Konum", tur: "nesne", altTip: "GeoCoordinates",
        alanlar: [
          { ad: "latitude", etiket: "Enlem", tur: "metin", ornek: "41.0082" },
          { ad: "longitude", etiket: "Boylam", tur: "metin", ornek: "28.9784" },
        ],
      },
      {
        ad: "openingHoursSpecification", etiket: "Çalışma saatleri", tur: "liste", altTip: "OpeningHoursSpecification", enAz: 1,
        alanlar: [
          { ad: "dayOfWeek", etiket: "Gün", tur: "secim", secenekler: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] },
          { ad: "opens", etiket: "Açılış", tur: "metin", ornek: "09:00" },
          { ad: "closes", etiket: "Kapanış", tur: "metin", ornek: "18:00" },
        ],
      },
    ],
  },
  {
    tip: "Organization", ad: "Kurum", aciklama: "Şirket/marka kimliği.",
    alanlar: [
      { ad: "name", etiket: "Kurum adı", tur: "metin", zorunlu: true },
      { ad: "url", etiket: "Web sitesi", tur: "url" },
      { ad: "logo", etiket: "Logo URL", tur: "url" },
      ADRES,
      { ad: "telephone", etiket: "Telefon", tur: "metin" },
      {
        ad: "sameAs", etiket: "Sosyal profiller", tur: "liste", enAz: 2,
        ipucu: "Google için güven sinyali — kurumun doğrulanabilir profilleri.",
        alanlar: [{ ad: "@deger", etiket: "Profil URL", tur: "url", ornek: "https://www.linkedin.com/company/..." }],
      },
    ],
  },
  {
    tip: "Person", ad: "Kişi", aciklama: "Yazar, uzman veya ekip üyesi.",
    alanlar: [
      { ad: "name", etiket: "Ad soyad", tur: "metin", zorunlu: true },
      { ad: "jobTitle", etiket: "Unvan", tur: "metin", ornek: "Plastik Cerrah" },
      { ad: "url", etiket: "Profil URL", tur: "url" },
      { ad: "image", etiket: "Fotoğraf URL", tur: "url" },
      { ad: "email", etiket: "E-posta", tur: "metin" },
      { ad: "telephone", etiket: "Telefon", tur: "metin" },
      {
        ad: "sameAs", etiket: "Sosyal profiller", tur: "liste", enAz: 1,
        alanlar: [{ ad: "@deger", etiket: "Profil URL", tur: "url" }],
      },
    ],
  },
  {
    tip: "Event", ad: "Etkinlik", aciklama: "Tarihli etkinlik veya seminer.",
    alanlar: [
      { ad: "name", etiket: "Etkinlik adı", tur: "metin", zorunlu: true },
      { ad: "startDate", etiket: "Başlangıç", tur: "tarihSaat", zorunlu: true },
      { ad: "endDate", etiket: "Bitiş", tur: "tarihSaat" },
      { ad: "description", etiket: "Açıklama", tur: "uzunMetin" },
      { ad: "image", etiket: "Görsel URL", tur: "url" },
      { ad: "eventAttendanceMode", etiket: "Katılım şekli", tur: "secim", secenekler: ["https://schema.org/OfflineEventAttendanceMode", "https://schema.org/OnlineEventAttendanceMode", "https://schema.org/MixedEventAttendanceMode"] },
      { ad: "eventStatus", etiket: "Durum", tur: "secim", secenekler: ["https://schema.org/EventScheduled", "https://schema.org/EventPostponed", "https://schema.org/EventCancelled"] },
      {
        ad: "location", etiket: "Konum", tur: "nesne", altTip: "Place", zorunlu: true,
        alanlar: [{ ad: "name", etiket: "Yer adı", tur: "metin" }, ADRES],
      },
      {
        ad: "offers", etiket: "Bilet", tur: "nesne", altTip: "Offer",
        alanlar: [
          { ad: "price", etiket: "Fiyat", tur: "metin" },
          { ad: "priceCurrency", etiket: "Para birimi", tur: "metin", ornek: "TRY" },
          { ad: "url", etiket: "Bilet URL", tur: "url" },
        ],
      },
    ],
  },
  {
    tip: "JobPosting", ad: "İş İlanı", aciklama: "Açık pozisyon ilanı.",
    alanlar: [
      { ad: "title", etiket: "Pozisyon", tur: "metin", zorunlu: true },
      { ad: "description", etiket: "İlan metni", tur: "uzunMetin", zorunlu: true },
      { ad: "datePosted", etiket: "Yayın tarihi", tur: "tarih", zorunlu: true },
      { ad: "validThrough", etiket: "Son başvuru", tur: "tarih" },
      { ad: "employmentType", etiket: "Çalışma şekli", tur: "secim", secenekler: ["FULL_TIME", "PART_TIME", "CONTRACTOR", "TEMPORARY", "INTERN"] },
      {
        ad: "hiringOrganization", etiket: "İşveren", tur: "nesne", altTip: "Organization", zorunlu: true,
        alanlar: [{ ad: "name", etiket: "Kurum adı", tur: "metin" }, { ad: "sameAs", etiket: "Web sitesi", tur: "url" }],
      },
      {
        ad: "jobLocation", etiket: "İş yeri", tur: "nesne", altTip: "Place", zorunlu: true,
        alanlar: [ADRES],
      },
    ],
  },
  {
    tip: "Recipe", ad: "Tarif", aciklama: "Yemek tarifi.",
    alanlar: [
      { ad: "name", etiket: "Tarif adı", tur: "metin", zorunlu: true },
      { ad: "image", etiket: "Görsel URL", tur: "url", zorunlu: true },
      { ad: "description", etiket: "Açıklama", tur: "uzunMetin" },
      { ad: "prepTime", etiket: "Hazırlık süresi", tur: "metin", ornek: "PT20M" },
      { ad: "cookTime", etiket: "Pişirme süresi", tur: "metin", ornek: "PT40M" },
      { ad: "recipeYield", etiket: "Kaç kişilik", tur: "metin", ornek: "4 porsiyon" },
      {
        ad: "recipeIngredient", etiket: "Malzemeler", tur: "liste", zorunlu: true, enAz: 3,
        alanlar: [{ ad: "@deger", etiket: "Malzeme", tur: "metin", ornek: "2 su bardağı un" }],
      },
      {
        ad: "recipeInstructions", etiket: "Yapılış", tur: "liste", altTip: "HowToStep", zorunlu: true, enAz: 2,
        alanlar: [{ ad: "text", etiket: "Adım", tur: "uzunMetin", zorunlu: true }],
      },
    ],
  },
  {
    tip: "VideoObject", ad: "Video", aciklama: "Sayfadaki video içeriği.",
    alanlar: [
      { ad: "name", etiket: "Video başlığı", tur: "metin", zorunlu: true },
      { ad: "description", etiket: "Açıklama", tur: "uzunMetin", zorunlu: true },
      { ad: "thumbnailUrl", etiket: "Kapak görseli URL", tur: "url", zorunlu: true },
      { ad: "uploadDate", etiket: "Yükleme tarihi", tur: "tarih", zorunlu: true },
      { ad: "duration", etiket: "Süre", tur: "metin", ipucu: "ISO 8601 — 5 dk 30 sn için PT5M30S.", ornek: "PT5M30S" },
      { ad: "contentUrl", etiket: "Video dosyası URL", tur: "url" },
      { ad: "embedUrl", etiket: "Gömme URL", tur: "url" },
    ],
  },
  // ── Sağlık ──
  {
    tip: "MedicalClinic", ad: "Klinik", aciklama: "Sağlık kuruluşu / klinik sayfası.",
    alanlar: [
      { ad: "name", etiket: "Klinik adı", tur: "metin", zorunlu: true },
      ADRES,
      { ad: "telephone", etiket: "Telefon", tur: "metin" },
      { ad: "url", etiket: "Web sitesi", tur: "url" },
      { ad: "image", etiket: "Görsel URL", tur: "url" },
      {
        ad: "medicalSpecialty", etiket: "Uzmanlık alanları", tur: "liste", enAz: 1,
        alanlar: [{ ad: "@deger", etiket: "Alan", tur: "metin", ornek: "PlasticSurgery" }],
      },
    ],
  },
  {
    tip: "Physician", ad: "Hekim", aciklama: "Doktor profili sayfası.",
    alanlar: [
      { ad: "name", etiket: "Ad soyad", tur: "metin", zorunlu: true },
      ADRES,
      { ad: "telephone", etiket: "Telefon", tur: "metin" },
      { ad: "image", etiket: "Fotoğraf URL", tur: "url" },
      {
        ad: "medicalSpecialty", etiket: "Uzmanlık", tur: "liste", enAz: 1,
        alanlar: [{ ad: "@deger", etiket: "Alan", tur: "metin", ornek: "Dermatology" }],
      },
    ],
  },
  {
    tip: "MedicalProcedure", ad: "Tıbbi İşlem", aciklama: "Tedavi veya operasyon sayfası.",
    alanlar: [
      { ad: "name", etiket: "İşlem adı", tur: "metin", zorunlu: true },
      { ad: "description", etiket: "Açıklama", tur: "uzunMetin" },
      { ad: "procedureType", etiket: "İşlem türü", tur: "secim", secenekler: ["https://schema.org/SurgicalProcedure", "https://schema.org/NoninvasiveProcedure", "https://schema.org/PercutaneousProcedure"] },
      { ad: "bodyLocation", etiket: "Vücut bölgesi", tur: "metin", ornek: "Burun" },
      { ad: "preparation", etiket: "Hazırlık", tur: "uzunMetin" },
      { ad: "followup", etiket: "Sonrası bakım", tur: "uzunMetin" },
      { ad: "howPerformed", etiket: "Nasıl uygulanır", tur: "uzunMetin" },
    ],
  },
];

/** Form değerlerinden JSON-LD kurar. Boş alanlar HİÇ eklenmez (boş string schema'yı kirletir). */
export function jsonLdKur(form: SchemaFormu, degerler: Record<string, unknown>): Record<string, unknown> {
  const bosMu = (v: unknown) =>
    v == null || v === "" || (Array.isArray(v) && v.length === 0) ||
    (typeof v === "object" && !Array.isArray(v) && Object.keys(v as object).length === 0);

  const alanDegeri = (a: Alan, ham: unknown): unknown => {
    if (a.tur === "nesne") {
      const ic: Record<string, unknown> = {};
      for (const alt of a.alanlar ?? []) {
        const d = alanDegeri(alt, (ham as Record<string, unknown>)?.[alt.ad]);
        if (!bosMu(d)) ic[alt.ad] = d;
      }
      if (Object.keys(ic).length === 0) return null;
      return a.altTip ? { "@type": a.altTip, ...ic } : ic;
    }
    if (a.tur === "liste") {
      const satirlar = Array.isArray(ham) ? ham : [];
      const cikti = satirlar.map((s) => {
        // Tek değerli liste (sameAs, malzemeler) — "@deger" anahtarı kullanılır
        if ((a.alanlar ?? []).length === 1 && a.alanlar![0]!.ad === "@deger") {
          return (s as Record<string, unknown>)?.["@deger"] ?? "";
        }
        const ic: Record<string, unknown> = {};
        for (const alt of a.alanlar ?? []) {
          const d = alanDegeri(alt, (s as Record<string, unknown>)?.[alt.ad]);
          if (!bosMu(d)) ic[alt.ad] = d;
        }
        if (Object.keys(ic).length === 0) return null;
        return a.altTip ? { "@type": a.altTip, ...ic } : ic;
      }).filter((x) => !bosMu(x));

      // Özel biçimler
      if (a.ad === "itemListElement") {
        return cikti.map((x, i) => ({ ...(x as object), position: i + 1 }));
      }
      if (a.ad === "mainEntity") {
        // FAQ: cevap Answer nesnesine sarılır
        return cikti.map((x) => {
          const q = x as Record<string, unknown>;
          return { "@type": "Question", name: q.name, acceptedAnswer: { "@type": "Answer", text: q.acceptedAnswer } };
        });
      }
      if (a.ad === "openingHoursSpecification") {
        return cikti.map((x) => ({ ...(x as object) }));
      }
      return cikti;
    }
    if (a.tur === "sayi") {
      const n = Number(ham);
      return Number.isFinite(n) && String(ham).trim() !== "" ? n : null;
    }
    return typeof ham === "string" ? ham.trim() : ham;
  };

  const govde: Record<string, unknown> = { "@context": "https://schema.org", "@type": form.tip };
  for (const a of form.alanlar) {
    const d = alanDegeri(a, degerler[a.ad]);
    if (!bosMu(d)) govde[a.ad] = d;
  }
  return govde;
}

/** Doldurulmamış ZORUNLU alanlar — canlı uyarı için. */
export function eksikZorunlular(form: SchemaFormu, degerler: Record<string, unknown>): string[] {
  const jsonLd = jsonLdKur(form, degerler);
  return form.alanlar.filter((a) => a.zorunlu && !(a.ad in jsonLd)).map((a) => a.etiket);
}
